// The raise sheet's decisions, kept out of the DOM so they can be tested.
//
// Three of them. What a tap on the tile grid does; whether the station has to
// be sealed into the session before the raise goes; and where the app lands
// once the server has answered. The sheet in `screens/phone/raise` draws
// what these decide and nothing more. (The step machine that used to be
// here went with the three-screen flow: frame v2.12 runs the raise as one
// sheet, and a sheet has no steps to be on.)

import type { AlertTypeKey } from '@/api/andon/types';
import type { GridItem } from './severity';

/** What tapping a cell of the grid does: picks a tile, or opens the WHS report and raises nothing. */
export type GridTap = { kind: 'pick'; type: AlertTypeKey } | { kind: 'safety' };

/**
 * The Safety cell carries no type key and never reaches the draft. This is
 * the whole of the rule the tile exists for — see `SAFETY_LINK_TILE` — put
 * where a test can hold it: there is no `AlertTypeKey` a Safety tap could
 * produce, so there is nothing the confirm card could be opened on.
 */
export function gridTap(item: GridItem): GridTap {
  return item.kind === 'safety' ? { kind: 'safety' } : { kind: 'pick', type: item.tile.key };
}

/** A station as the device and the session each hold it. */
export interface StationRef {
  workcentreId: number;
  flowlineId: number | null;
}

/**
 * Whether the station has to be sealed into the session before the raise.
 *
 * The andon module reads the station off the operator cookie, never off the
 * raise body — a client that could name its own work centre could raise an
 * alert claiming to be somewhere it is not. The shell seals it whenever the
 * device's station changes, but a seal is one request that can fail quietly
 * (the module was dormant, the network dropped, the cookie lapsed), and the
 * raise is the one moment it has to have landed.
 *
 *   none    the device has no station at all. Nothing can be sealed; the
 *           server will refuse the raise, in its own words, and the flow
 *           shows them.
 *   seal    the session does not hold what the device holds — nothing sealed,
 *           a different station, or home never loaded so it is not known.
 *           Seal first, then raise.
 *   sealed  the session already agrees. Raise straight away.
 *
 * `sealed` undefined is "unknown" and treated as `seal`: one extra round trip
 * on a device whose home has not landed is cheaper than a refusal on a
 * stopped line.
 */
export type SealPlan = 'none' | 'seal' | 'sealed';

export function stationSealNeeded(device: StationRef | null, sealed: StationRef | null | undefined): SealPlan {
  if (!device) return 'none';
  if (!sealed) return 'seal';
  return sealed.workcentreId === device.workcentreId && sealed.flowlineId === device.flowlineId ? 'sealed' : 'seal';
}

/**
 * Where the app goes once the raise has been answered.
 *
 * The alert exists either way — the server answered 2xx, and it is in Odoo.
 * What is in doubt is whether this device can open it, and being sent to a
 * sheet that needs an id it was not given is what left the operator looking
 * at nothing. So an unusable id lands on the Andon tab with a line saying what
 * happened, rather than on an empty sheet.
 */
export type RaiseLanding = { kind: 'alert'; id: number } | { kind: 'tab' };

export function raiseLanding(alert: { id?: unknown } | null | undefined): RaiseLanding {
  const id = alert?.id;
  return typeof id === 'number' && Number.isInteger(id) && id > 0 ? { kind: 'alert', id } : { kind: 'tab' };
}
